import { useEffect } from 'react';

// Flag global para evitar doble inyección
let themeScriptInjected = false;

/**
 * Recrea un <script> parseado como nodo ejecutable.
 * Los <script> insertados vía innerHTML NO se ejecutan; hay que
 * clonarlos en un elemento <script> nuevo creado con createElement.
 */
function createExecutableScript(sourceScript, index) {
  const script = document.createElement('script');

  // Copiar atributos (src, type, async, defer, etc.)
  Array.from(sourceScript.attributes).forEach((attr) => {
    script.setAttribute(attr.name, attr.value);
  });

  // Copiar código inline si lo hay
  if (sourceScript.textContent) {
    script.textContent = sourceScript.textContent;
  }

  script.dataset.themeScript = String(index);
  return script;
}

/**
 * Crea un <script src> ejecutable a partir de un <link rel="preload" as="script">.
 * Un preload solo descarga el recurso; no lo ejecuta. Para correr el menú
 * hay que crear el <script> real apuntando al mismo href.
 */
function createScriptFromPreload(link, index) {
  const href = link.getAttribute('href');
  if (!href) return null;

  const script = document.createElement('script');
  script.src = href;
  script.async = false; // preservar orden de ejecución
  script.dataset.themeScript = String(index);
  return script;
}

/**
 * Componente que inyecta el/los script del theme de WordPress
 * provenientes del campo `script` de theme_templates.json.
 *
 * Soporta dos formatos:
 *   - <script src="..."> o <script>inline</script>
 *   - <link rel="preload" as="script" href="..."> (se convierte en <script src>)
 *
 * NOTA: temporalmente NO se carga la copia local public/js/theme-main.js.
 * La funcionalidad del menú ahora llega desde el JSON del theme.
 *
 * @param {string} script - HTML con los scripts/preloads del theme.
 */
export default function ScriptInjector({ script }) {
  useEffect(() => {
    if (themeScriptInjected) return;
    if (!script) return;

    // Evitar duplicados si el efecto se re-ejecuta (StrictMode)
    if (document.querySelector('[data-theme-script]')) return;

    // Parsear el HTML del campo script sin ejecutarlo todavía
    const doc = new DOMParser().parseFromString(script, 'text/html');

    const nodes = [];
    let index = 0;

    // 1) <script> explícitos
    doc.querySelectorAll('script').forEach((sourceScript) => {
      nodes.push(createExecutableScript(sourceScript, index++));
    });

    // 2) <link rel="preload" as="script"> -> <script src>
    doc.querySelectorAll('link[rel="preload"][as="script"]').forEach((link) => {
      const executable = createScriptFromPreload(link, index++);
      if (executable) nodes.push(executable);
    });

    if (nodes.length === 0) return;

    themeScriptInjected = true;
    nodes.forEach((node) => document.body.appendChild(node));
  }, [script]);

  return null;
}
